import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert,
  Select, MenuItem, FormControl, InputLabel, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function InventoryUI() {

  const [inventories, setInventories] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);

  const loadInventories = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.inventoryApi.get(`/?page=${pageNum}&size=${size}`);
      const payload = res.data.data || {};
      setInventories(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching inventories", err);
    }
  };

  useEffect(() => {
    loadInventories();
    // loadProducts();
  }, [page, rowsPerPage]);

  const handleOpen = (inv = {}) => {
    setForm({
      id: inv.id || "",
      // product: inv.product?.id || "",
      product: inv.product || "",
      quantityAvailable: inv.quantityAvailable || "",
      quantityReserved: inv.quantityReserved || "",
      reorderLevel: inv.reorderLevel || "",
      location: inv.location || "",
      inv_status: inv.inv_status || "AVAILABLE",
    });
    setEditing(!!inv.id);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({});
    setOpen(false);
    setFile(null);
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      if (file) {
        // ✅ Check file type
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv" // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({ open: true, message: "Only Excel or CSV files are allowed!", severity: "error" });
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        // ✅ Upload inventory file
        await axiosInstances.inventoryApi.post(`/uploadInventories`, formData, {
          headers: {
            Authorization: `Bearer ${token}`
          },
        });

        setSnackbar({ open: true, message: "Inventory file uploaded successfully!", severity: "success" });
      } else {
        // ✅ Manual form submission
        const inventoryPayload = { ...form };

        if (editing) {
          await axiosInstances.inventoryApi.post(`/update`, inventoryPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Inventory updated successfully!", severity: "success" });
        } else {
          await axiosInstances.inventoryApi.post(`/save`, inventoryPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Inventory created successfully!", severity: "success" });
        }
      }

      loadInventories(); // 🔁 Refresh inventory list
      handleClose();     // ❌ Close dialog/modal
      setFile(null);     // 🧹 Reset file input
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({ open: true, message: "Duplicate inventory entry", severity: "error" });
      } else if (err.response && err.response.status === 401) {
        setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.inventoryApi.post(`/delete`, { id });
        loadInventories();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Inventory Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add Inventory
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Available Qty</TableCell>
              <TableCell>Reserved Qty</TableCell>
              <TableCell>Reorder Level</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventories.map((inv, index) => (
              <TableRow key={inv.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{inv.id}</TableCell>
                <TableCell>{inv.product}</TableCell>
                <TableCell>{inv.quantityAvailable}</TableCell>
                <TableCell>{inv.quantityReserved}</TableCell>
                <TableCell>{inv.reorderLevel}</TableCell>
                <TableCell>{inv.location}</TableCell>
                <TableCell>{inv.inv_status}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(inv)}><Edit /></IconButton>
                  {pendingDeletes[inv.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(inv.id)}><Undo /></IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(inv.id)}><Delete /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {inventories.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">No inventory records found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Inventory" : "Add Inventory"}</DialogTitle>
        <DialogContent>
          {editing && (
            <TextField fullWidth margin="dense" label="ID" value={form.id || ""} disabled />
          )}

          <TextField fullWidth margin="dense" label="Product" name="product" type="number" value={form.product || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Quantity Available" name="quantityAvailable" type="number" value={form.quantityAvailable || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Quantity Reserved" name="quantityReserved" type="number" value={form.quantityReserved || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Reorder Level" name="reorderLevel" type="number" value={form.reorderLevel || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Location" name="location" value={form.location || ""} onChange={handleChange} />

          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select name="inv_status" value={form.inv_status || ""} onChange={handleChange}>
              <MenuItem value="AVAILABLE">Available</MenuItem>
              <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
              <MenuItem value="RESERVED">Reserved</MenuItem>
              <MenuItem value="DISCONTINUED">Discontinued</MenuItem>
            </Select>
          </FormControl>
          {!editing && (
                      <>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          Upload File
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                        {file && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {file.name}
                          </Typography>
                        )}
                      </>
                    )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
